import React, { useEffect, useState } from 'react';

import {
  SatisfactionSurveyView,
  answerSatisfactionSurvey,
  getSatisfactionSurvey,
} from '../api/satisfaction-survey';
import { UTC_ZONE, familyZoneLabel, formatShortDateTime } from '../time/zonedTime';

/**
 * MC-06 §6 — one private, optional question to the parent of record once a
 * cycle of classes has ended.
 *
 * What the copy promises is what the server enforces: the answer is private
 * (never a public review), the teacher never sees it, it can be changed until
 * the window closes, and nothing depends on it — no discount for a good score,
 * no consequence for a bad one, and no pressure to answer (MC06-R17).
 *
 * Draws nothing while the cycle is still running, and nothing at all if the
 * survey cannot be read: an optional question is not worth an error on the
 * family's screen.
 */
const SatisfactionSurvey: React.FC<{ enrollmentId: string }> = ({ enrollmentId }) => {
  const [survey, setSurvey] = useState<SatisfactionSurveyView | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSatisfactionSurvey(enrollmentId)
      .then((s) => {
        if (cancelled) return;
        setSurvey(s);
        setSelected(s.answer?.score ?? null);
      })
      .catch(() => !cancelled && setSurvey(null));
    return () => {
      cancelled = true;
    };
  }, [enrollmentId]);

  if (!survey || survey.status === 'NOT_AVAILABLE') return null;

  const open = survey.status === 'OPEN';

  const submit = async () => {
    if (selected === null) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const next = await answerSatisfactionSurvey(enrollmentId, selected);
      setSurvey(next);
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || 'No pudimos guardar tu respuesta. Inténtalo otra vez.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="satisfaction-title" style={boxStyle}>
      <h3 id="satisfaction-title" style={{ marginTop: 0 }}>
        Tu opinión sobre este ciclo
      </h3>
      <p>
        Es privada: no se publica como reseña y el profesor no la ve. Responder es opcional y no
        cambia nada de tu inscripción ni de los pagos.
      </p>
      <fieldset disabled={!open || busy} style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontWeight: 600, marginBottom: 8 }}>{survey.question.text}</legend>
        {survey.question.scale.map((option) => (
          <label key={option.value} style={{ display: 'block', margin: '4px 0' }}>
            <input
              type="radio"
              name={`satisfaction-${enrollmentId}`}
              value={option.value}
              checked={selected === option.value}
              onChange={() => setSelected(option.value)}
            />{' '}
            {option.value} · {option.label}
          </label>
        ))}
      </fieldset>
      {open ? (
        <>
          <button
            onClick={submit}
            disabled={busy || selected === null || selected === survey.answer?.score}
            style={{ marginTop: 8 }}
          >
            {survey.answer ? 'Cambiar mi respuesta' : 'Enviar respuesta'}
          </button>
          {survey.closesAt ? (
            <p style={{ fontSize: 13, color: '#495057' }}>
              Puedes responder o cambiar tu respuesta hasta el{' '}
              {formatShortDateTime(survey.closesAt, UTC_ZONE)}. {familyZoneLabel(UTC_ZONE)}
            </p>
          ) : null}
        </>
      ) : (
        <p style={{ fontSize: 13, color: '#495057' }}>
          {survey.answer
            ? 'La encuesta ya cerró. Guardamos tu respuesta.'
            : 'La encuesta ya cerró sin respuesta. No pasa nada.'}
        </p>
      )}
      {saved ? <p role="status">Gracias. Guardamos tu respuesta.</p> : null}
      {error ? <p role="alert">{error}</p> : null}
    </section>
  );
};

const boxStyle: React.CSSProperties = {
  border: '1px solid #dee2e6',
  borderRadius: 8,
  padding: 16,
  margin: '24px 0',
};

export default SatisfactionSurvey;
